<template>
    <v-dialog v-model="dialog" scrollable max-width="850">
        <template v-slot:activator="{ props }">
            <v-btn
                v-bind="props"
                color="secondary"
                variant="outlined"
                rounded
                v-if="isVersionUpdate === 0"
                class="text-capitalize"
            >
                Add New Library
                <v-icon style="margin-left: 5px">mdi-plus</v-icon>
            </v-btn>
            <v-btn
                v-bind="props"
                icon
                v-if="isVersionUpdate === 1"
                color="secondary"
                class="text-capitalize text-typography3"
            >
                <v-icon size="x-large" color="secondary"> mdi-pencil </v-icon>
            </v-btn>
        </template>
        <v-card class="libform">
            <v-card-title>
                <h1>
                    {{ headerText }}
                </h1>
            </v-card-title>

            <v-card-text class="pa-0">
                <v-stepper v-model="stepper" color="accent">
                    <v-stepper-header>
                        <v-stepper-item
                            color="accent"
                            :complete="stepper > 1"
                            :value="1"
                            >Start</v-stepper-item
                        >
                        <v-divider></v-divider>
                        <v-stepper-item
                            color="accent"
                            :complete="stepper > 2"
                            :value="2"
                            >Language</v-stepper-item
                        >
                        <v-divider></v-divider>
                        <v-stepper-item color="accent" :value="3"
                            >Details</v-stepper-item
                        >
                    </v-stepper-header>

                    <v-progress-linear
                        indeterminate
                        color="accent"
                        v-if="showProgressNewLib"
                    ></v-progress-linear>

                    <v-stepper-window>
                        <v-stepper-window-item :value="1">
                            <v-card-text>
                                <p>
                                    {{ introText }}
                                    <br />
                                    <br />Check out the
                                    <a href="/docs/contributing"
                                        >documentation</a
                                    >
                                    if you haven't one so already!
                                </p>
                            </v-card-text>

                            <v-btn
                                color="secondary"
                                @click="stepper = 2"
                                style="letter-spacing: 1.5px"
                                >Next</v-btn
                            >
                        </v-stepper-window-item>

                        <v-stepper-window-item :value="2">
                            <v-card-text class="pt-4 px-4">
                                <p>
                                    Select what language your library/function
                                    is written in:
                                </p>
                                <v-radio-group
                                    v-model="techRadioGroup"
                                    hide-details
                                >
                                    <v-radio
                                        color="secondary"
                                        label="Python"
                                        value="python"
                                    ></v-radio>
                                    <v-radio
                                        v-if="net10Available"
                                        color="secondary"
                                        label=".NET 10"
                                        value="6"
                                    ></v-radio>
                                    <v-radio
                                        color="secondary"
                                        label=".NET Core 3.1"
                                        value="3"
                                    ></v-radio>
                                    <v-radio
                                        color="secondary"
                                        label=".NET Core 2.2 (or below)"
                                        value="2"
                                    ></v-radio>
                                    <v-radio
                                        color="secondary"
                                        label=".NET Framework 4.6 (or below)"
                                        value="1"
                                    ></v-radio>
                                </v-radio-group>
                            </v-card-text>

                            <v-btn
                                variant="text"
                                @click="stepper = 1"
                                style="letter-spacing: 1.5px"
                                >Back</v-btn
                            >
                            <v-btn
                                color="secondary"
                                @click="stepper = 3"
                                style="letter-spacing: 1.5px"
                                >Next</v-btn
                            >
                        </v-stepper-window-item>

                        <v-stepper-window-item :value="3">
                            <v-form
                                ref="form"
                                lazy-validation
                                @submit.prevent="submitForm()"
                            >
                                <v-container>
                                    <v-row>
                                        <v-col
                                            cols="12"
                                            class="pr-5"
                                            v-if="kubeAlertEnabled"
                                        >
                                            <v-alert
                                                type="warning"
                                                variant="text"
                                                border="start"
                                                class="mx-4 mb-0"
                                            >
                                                Kubernetes support is currently
                                                experimental, please use it only
                                                for testing. Set Azure Function
                                                as the Publish Location for
                                                project use.
                                            </v-alert>
                                        </v-col>
                                        <p v-if="isVersionUpdate === 1">
                                            {{
                                                `The most recent stable version was ${recentVersion()}, new versions must increment by at least one PATCH level.`
                                            }}
                                        </p>
                                        <v-col cols="6" class="pr-5">
                                            <v-text-field
                                                :disabled="
                                                    showProgressNewLib ||
                                                    isVersionUpdate === 1
                                                "
                                                v-model="lib.name"
                                                label="Library Name"
                                                color="secondary"
                                                placeholder="Sample Library"
                                                required
                                                :rules="reqFieldRule"
                                                @input="
                                                    lib.name = lib.name.trim()
                                                "
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="6" class="pr-5">
                                            <v-text-field
                                                :disabled="
                                                    showProgressNewLib ||
                                                    isVersionUpdate === 1
                                                "
                                                v-model="lib.slug"
                                                label="Slug"
                                                placeholder="samplelibrary"
                                                color="secondary"
                                                required
                                                :rules="libslugRules"
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="6" class="pr-5">
                                            <v-text-field
                                                :disabled="
                                                    showProgressNewLib ||
                                                    isVersionUpdate === 1
                                                "
                                                v-model="lib.assembly"
                                                color="secondary"
                                                :label="assemblyLabel"
                                                :placeholder="
                                                    assemblyPlaceholder
                                                "
                                                required
                                                :rules="reqFieldRule"
                                                @input="
                                                    lib.assembly =
                                                        lib.assembly.trim()
                                                "
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="6" class="pr-5">
                                            <v-text-field
                                                :disabled="showProgressNewLib"
                                                v-model="version"
                                                color="secondary"
                                                label="Version"
                                                :placeholder="recentVersion()"
                                                required
                                                :rules="versionRules"
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                        <v-col
                                            cols="6"
                                            class="pr-5"
                                            v-if="publishLocationEnabled"
                                        >
                                            <v-combobox
                                                label="Publish Location"
                                                v-model="
                                                    selectedPublishLocation
                                                "
                                                :items="publishLocationOptions"
                                                item-title="label"
                                                item-value="value"
                                                :required="
                                                    publishLocationEnabled
                                                "
                                                @update:modelValue="
                                                    onPublishLocationChange
                                                "
                                                hide-details
                                            ></v-combobox>
                                        </v-col>
                                        <v-col
                                            cols="6"
                                            class="pr-5"
                                            v-if="publishLocationEnabled"
                                        >
                                            <v-combobox
                                                label="OS"
                                                v-model="selectedOs"
                                                :items="osOptions"
                                                item-title="label"
                                                item-value="value"
                                                :required="selectedOsEnabled()"
                                                :disabled="!selectedOsEnabled()"
                                                hide-details
                                            ></v-combobox>
                                        </v-col>
                                        <v-col cols="12" class="pr-5">
                                            <v-textarea
                                                :disabled="showProgressNewLib"
                                                color="secondary"
                                                v-model="description"
                                                label="Description"
                                                required
                                                :rules="reqFieldRule"
                                                @input="
                                                    description =
                                                        description.trim()
                                                "
                                                hide-details
                                            ></v-textarea>
                                        </v-col>
                                        <v-col cols="4" class="pr-5 pt-3">
                                            <v-btn
                                                block
                                                color="secondary"
                                                @click="pickFile"
                                                :disabled="showProgressNewLib"
                                                style="letter-spacing: 1.5px"
                                            >
                                                <v-icon left
                                                    >mdi-paperclip</v-icon
                                                >Browse...
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="8" class="pt-3 pr-5">
                                            <v-text-field
                                                :disabled="showProgressNewLib"
                                                v-model="zipfileName"
                                                required
                                                :rules="zipfileRules"
                                                readonly
                                                color="secondary"
                                                variant="solo"
                                                clearable
                                                @click:clear="cleared()"
                                                hide-details
                                            ></v-text-field>
                                            <input
                                                type="file"
                                                style="display: none"
                                                ref="fileinput"
                                                accept="application/zip"
                                                color="secondary"
                                                @change="onFilePicked"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                </v-container>

                                <v-btn
                                    variant="text"
                                    @click="stepper = 2"
                                    :disabled="showProgressNewLib"
                                    style="letter-spacing: 1.5px"
                                    >Back</v-btn
                                >
                                <v-btn
                                    color="secondary"
                                    type="submit"
                                    :loading="showProgressNewLib"
                                    :disabled="
                                        showProgressNewLib || !isFormValid()
                                    "
                                    :style="{
                                        letterSpacing: '1.5px',
                                        opacity:
                                            showProgressNewLib || !isFormValid()
                                                ? 0.6
                                                : 1,
                                    }"
                                >
                                    Upload
                                </v-btn>
                            </v-form>
                        </v-stepper-window-item>
                    </v-stepper-window>
                </v-stepper>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
const isSemver = require('is-semver')

const props = defineProps({
    library: {
        required: false,
        default: () => ({}),
    },
    libraries: {
        required: true,
        type: Array,
    },
})

const store = useStore()
const slugregex = RegExp('^[a-z0-9](-?[a-z0-9])*$')

// Template refs
const form = ref(null)
const fileinput = ref(null)

// State
const lib = ref({ ...props.library })
const description = ref(setDesc())
const version = ref(recentVersion())
const dialog = ref(false)
const stepper = ref(1)
const techRadioGroup = ref(recentFramework())
const zipfile = ref(null)
const zipfileName = ref(null)
const zipfileUrl = ref(null)
const net10Available = process.env.VUE_APP_NET10_AVAILABLE === 'true'

const selectedPublishLocation = ref({
    label: 'Azure Function',
    value: 'AzureFunc',
})
const publishLocationOptions = [
    { label: 'Azure Function', value: 'AzureFunc' },
    { label: 'Kubernetes', value: 'Kubernetes' },
]
const selectedOs = ref({ label: 'Linux', value: 'linux' })
const osOptions = [
    { label: 'Linux', value: 'linux' },
    { label: 'Windows', value: 'windows' },
]

const reqFieldRule = [
    (v) => !!v || 'This field is required',
    (v) => (v && !!v.trim()) || 'Empty spaces are not allowed',
]
const reqFieldRule2 = [
    (v) => Object.entries(v).length > 0 || 'This field is required',
]
const libslugRules = [
    (v) => !!v || 'Slug is required',
    (v) =>
        (v && v.length <= 25) ||
        'Slug must be less than 25 characters in length',
    (v) => (v && !v.includes('-')) || 'Slug cannot contain hyphens',
    (v) =>
        slugregex.test(v) || 'Only lowercase letters and numbers are allowed',
    (v) =>
        props.libraries.findIndex((x) => x.slug === v) === -1 ||
        isVersionUpdate.value === 1 ||
        'Slug already in use',
]
const zipfileRules = [
    () => zipfile.value !== null || 'Library zip file is required',
]
const versionRules = [
    (v) => !!v || 'Version is required',
    (v) => isSemver(v) || 'Version is not valid',
]
const apiRules = [
    (v) => !!v || 'URL is required',
    (v) => (!!v && !v.includes(' ')) || 'Cannot contain spaces',
    (v) =>
        (!!v && !v.startsWith('/') && !v.endsWith('/')) ||
        'Cannot start or end with /',
]

// Computed
const showProgressNewLib = computed(() => store.state.showProgressNewLib)

const isVersionUpdate = computed(() => (lib.value.libraryId ? 1 : 0))

const isPython = computed(() => techRadioGroup.value === 'python')

const assemblyLabel = computed(() =>
    isPython.value ? 'Entry file' : 'Assembly'
)

const assemblyPlaceholder = computed(() =>
    isPython.value ? 'main.py' : 'SampleLibrary.dll'
)

const introText = computed(() => {
    if (isVersionUpdate.value === 0) {
        return 'Add new a new library or function to ArupCompute and make it accessible to the rest of Arup.'
    }
    return `Updating library: ${lib.value.name}`
})

const headerText = computed(() => {
    if (isVersionUpdate.value === 0) return 'Add a new library'
    return `Update ${lib.value.name}`
})

const publishLocationEnabled = computed(
    () =>
        process.env.VUE_APP_FEATURE_KUBE_ENABLED === 'true' &&
        techRadioGroup.value === '6'
)

const kubeAlertEnabled = computed(
    () =>
        publishLocationEnabled.value &&
        process.env.VUE_APP_FEATURE_KUBE_ALERT_ENABLED === 'true' &&
        selectedPublishLocation.value &&
        selectedPublishLocation.value.value === 'Kubernetes'
)

// Methods
function recentVersion() {
    if (props.library !== {} && props.library.libraryVersions) {
        return props.library.libraryVersions[0].version
    }
    return ''
}

function recentFramework() {
    return 0
}

function setDesc() {
    if (props.library !== {} && props.library.libraryVersions) {
        return props.library.libraryVersions[0].description
    }
    return ''
}

function onPublishLocationChange() {
    if (selectedPublishLocation.value.value === 'AzureFunc') {
        selectedOs.value = { label: 'Linux', value: 'linux' }
    }
}

function isFormValid() {
    let res =
        lib.value.name &&
        lib.value.slug &&
        lib.value.assembly &&
        version.value &&
        description.value &&
        zipfileName.value

    if (publishLocationEnabled.value) {
        res = res && selectedPublishLocation.value && selectedOs.value
    }

    return res
}

function pickFile() {
    fileinput.value.click()
}

function onFilePicked(e) {
    const files = e.target.files
    if (files[0] !== undefined) {
        zipfileName.value = files[0].name
        if (zipfileName.value.lastIndexOf('.') <= 0) return
        const fr = new FileReader()
        fr.readAsDataURL(files[0])
        fr.addEventListener('load', () => {
            zipfileUrl.value = fr.result
            zipfile.value = files[0]
            form.value.validate()
        })
    } else {
        zipfileName.value = null
        zipfile.value = null
        zipfileUrl.value = null
    }
}

function cleared() {
    zipfileName.value = null
    zipfile.value = null
    zipfileUrl.value = null
    try {
        fileinput.value.value = ''
        if (fileinput.value.value) {
            fileinput.value.type = 'text'
            fileinput.value.type = 'file'
        }
    } catch (e) {}
    form.value.validate()
}

function selectedOsEnabled() {
    return (
        publishLocationEnabled.value &&
        selectedPublishLocation.value &&
        selectedPublishLocation.value.value === 'Kubernetes'
    )
}

async function submitForm() {
    const { valid } = await form.value.validate()
    if (!valid) return

    lib.value.version = version.value
    lib.value.description = description.value
    lib.value.libraryId =
        isVersionUpdate.value === 1 ? props.library.libraryId : '0'
    const framework = techRadioGroup.value

    await store.dispatch('createOrUpdateLib', {
        lib: lib.value,
        libstream: zipfile.value,
        isVersionUpdate: isVersionUpdate.value,
        framework,
        publishLocation:
            selectedPublishLocation.value && publishLocationEnabled.value
                ? selectedPublishLocation.value.value
                : 'AzureFunc',
        os:
            selectedOs.value && publishLocationEnabled.value
                ? selectedOs.value.value
                : 'linux',
    })

    dialog.value = false
}
</script>
<style scoped>
.libform {
    padding: 10px 20px 20px 20px;
}
</style>
